import AddIcon from '@mui/icons-material/Add';
import DashboardCustomizeOutlinedIcon from '@mui/icons-material/DashboardCustomizeOutlined';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import { Box, Grid, ListItemButton, Stack } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { FaLayerGroup } from 'react-icons/fa';
import { MdDashboardCustomize } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import {
  IComposableDashboardCollectionsContext,
  IComposableDashboardContext,
  IHomeRecruiterAnalytics,
  IPinnedDashboard,
  IPinnedDashboardCollection,
  getMyPinnedDashboards,
  getMyPinnedDashboardsCollection,
  getRecruiterAnalyticsStatus,
} from 'wisestep-api';
import {
  ScrollableTabs,
  WsButton,
  WsElementWithPopOver,
  WsIconText,
  WsTypoVariants,
} from 'wisestep-base';

import { PinDashboardCollectionButton } from '../../../../src/components';
import NoDataFoundCard from '../../../components/noDataFoundCard';
import {
  defaultPinRecruiterAnalyticsData,
  pinRecruiterAnalytics,
} from '../../../components/pinDashboardCollectionButton/PinDashboardHelper';
import { useUserContext, useWorkspaceContext } from '../../../context';
import AnalyticsDashboardViewer from '../../../features/analyticsPage/components/dashboard/AnalyticsDashboardViewer';
import RenderDashboardsTabs from '../../../features/analyticsPage/components/dashboard/RenderDashboardsTabs';
import ComposableDashboardFiltersContextProvider from '../../../features/analyticsPage/context/ComposableDashboardFiltersContext';
import { useComposableDashboardCollectionsContext } from '../../../features/analyticsPage/hooks/useComposableDashboardCollectionsContext';
import { useComposableDashboardsContext } from '../../../features/analyticsPage/hooks/useComposableDashboardsContext';
import { getAccountManagerAnalyticsUrl } from '../../../features/candidates/utils/CandidateHelper';
import UserProfileOverviewTemp from '../../../features/recruiterProfileDetails/components/userProfile/UserProfileOverviewTemp';
import StageStatusContextProvider from '../../../features/submissions/context/StageStatusContext';
import ErrorLogger from '../../../providers/ErrorLogger';
import { useNotificationStore } from '../../../utils/NotificationStore';
import {
  generateTab,
  IPinnedEntities,
  IPinnedEntityEnum,
  IPinnedEntityTab,
  IPinnedTabName,
} from '../utils/HomeDashboardHelper';

import ReminderBoard from './ReminderBoard';
import UnpinDashboard from './UnpinDashboard';
import UserGuide from './UserGuide';

interface HomePageDashBoardProps {
  isDashboard: (value: any) => void;
}

const recruiterAnalyticsTab = generateTab(
  IPinnedEntityEnum.RECRUITER_ANALYTICS,
  IPinnedTabName.RECRUITER_ANALYTICS
);
const HomePageDashBoard = ({ isDashboard }: HomePageDashBoardProps) => {
  const {
    selectedDashboard,
    setSelectedDashboardByIndex,
    dashboards: customDashboards,
  } = useComposableDashboardsContext() as Required<IComposableDashboardContext>;

  const { setSelectedCollectionByIndex, dashboardCollections } =
    useComposableDashboardCollectionsContext() as Required<IComposableDashboardCollectionsContext>;
  const workspace = useWorkspaceContext();
  const isTargetWorkspace = workspace.id === 'a61ed280-61fa-4e19-8e54-88bffcc24a75';
  const homeTab = generateTab(
    IPinnedEntityEnum.HOME,
    isTargetWorkspace ? IPinnedTabName.REMINDERS : IPinnedTabName.HOME
  );
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [selectedPinnedId, setSelectedPinnedId] = useState<string>('');
  const [entitesTabList, setEntitiesTabList] = useState<IPinnedEntityTab[]>([homeTab]);
  const pinned = useRef<IPinnedEntityTab[]>([homeTab]);
  const [pinnedEntities, setPinnedEntities] = useState<IPinnedEntities>({
    collections: [],
    dashboards: [],
  });
  const { collections, dashboards } = pinnedEntities;
  const navigate = useNavigate();
  const setNotification = useNotificationStore((state) => state.setNotification);
  const entityAfterUnpin = useRef<{ tab: IPinnedEntityTab | null; entityId: string | null }>({
    tab: null,
    entityId: null,
  });
  const [homeRecruiterAnalyticsStatus, setHomeRecruiterAnalyticsStatus] = useState<boolean>(false);
  const isSelectedTabHome = selectedTab === 0;
  const isSelectedTabEntitiesLast = selectedTab === entitesTabList.length - 1;
  const [pinRecruiterAnalyticsData, setPinRecruiterAnalyticsData] =
    useState<IHomeRecruiterAnalytics>(defaultPinRecruiterAnalyticsData);
  const user = useUserContext();
  useEffect(() => {
    const fetchPinnedEntities = async () => {
      try {
        if (sessionStorage.getItem('sessionCandidateProfileRedirectUrlCorpHiring')) {
          window.location.href = sessionStorage.getItem(
            'sessionCandidateProfileRedirectUrlCorpHiring'
          ) as string;
        } else {
          sessionStorage.removeItem('sessionCandidateProfileRedirectUrlCorpHiring');
          const collections = await getMyPinnedDashboardsCollection();
          const dashboards = await getMyPinnedDashboards();
          setPinnedEntities({ dashboards: dashboards.data, collections: collections.data });
        }
      } catch (error) {
        console.log(error);
      }
    };
    fetchPinnedEntities();
  }, []);
  const setTabFromEntityType = useCallback(
    (tab: IPinnedEntityTab | null, index: number, entityId: string | null, filters?: any) => {
      switch (tab?.tabType) {
        case IPinnedEntityEnum.DASHBOARD_COLLECTION:
          {
            const dbIndex = dashboardCollections?.findIndex((item) => item.id === entityId);
            setSelectedCollectionByIndex(dbIndex, true, filters);
            setSelectedPinnedId(tab?.id as string);
            setSelectedTab(index);
          }
          break;
        case IPinnedEntityEnum.DASHBOARD:
          {
            const dbIndex = customDashboards?.findIndex((item) => item.id === entityId);
            setSelectedPinnedId(tab?.id as string);
            setSelectedDashboardByIndex(dbIndex);
            setSelectedTab(index);
          }
          break;
        case IPinnedEntityEnum.HOME:
        case IPinnedEntityEnum.RECRUITER_ANALYTICS:
          setSelectedPinnedId('');
          setSelectedTab(index);
          break;
      }
      if (
        entityAfterUnpin.current.entityId !== null ||
        entityAfterUnpin.current.tab?.tabType === IPinnedEntityEnum.HOME
      )
        entityAfterUnpin.current = { tab: null, entityId: null };
    },
    [
      customDashboards,
      dashboardCollections,
      setSelectedCollectionByIndex,
      setSelectedDashboardByIndex,
    ]
  );

  const handleSelectedTab = (index: number) => {
    let filters;
    const validIndex = Math.min(index, pinned.current.length - 1);
    const currentTab = pinned.current[validIndex];
    const getEntityId = (tab: IPinnedEntityTab) => {
      switch (tab.tabType) {
        case IPinnedEntityEnum.HOME:
        case IPinnedEntityEnum.RECRUITER_ANALYTICS:
          return null;
        case IPinnedEntityEnum.DASHBOARD_COLLECTION: {
          const entity = collections.find((pc) => tab.id === pc.id);
          filters = entity?.filters;
          return entity?.dashboardCollection.id ?? null;
        }
        case IPinnedEntityEnum.DASHBOARD:
          return dashboards.find((pd) => pd.id === tab.id)?.dashboard.id ?? null;
      }
    };
    setTabFromEntityType(currentTab, validIndex, getEntityId(currentTab), filters);
  };
  useEffect(() => {
    if (entityAfterUnpin.current.tab !== null) {
      const { tab, entityId } = entityAfterUnpin.current;
      const tabIdIndex = entitesTabList.findIndex((v) => v.id === tab.id);
      const filters = collections?.find((v) => v.id === tab.id)?.filters;
      setTabFromEntityType(tab, tabIdIndex !== -1 ? tabIdIndex : 0, entityId, filters);
    }
  }, [entitesTabList, setTabFromEntityType]);

  useEffect(() => {
    const tabsDashboard = dashboards.map((pd, index) => ({
      tabName: pd.dashboard?.name,
      index,
      tabType: IPinnedEntityEnum.DASHBOARD,
      icon: <MdDashboardCustomize />,
      id: pd.id as string,
    }));
    const tabsCollection = collections.map(async (pd, index) => {
      const tab: IPinnedEntityTab = {
        tabName: pd.entityName ?? pd.dashboardCollection?.name,
        index,
        tabType: IPinnedEntityEnum.DASHBOARD_COLLECTION,
        icon: <FaLayerGroup />,
        id: pd.id as string,
      };
      if (pd.dashboardCollection.assignToAccountManagers) {
        const url = await getAccountManagerAnalyticsUrl(pd.entityId ?? '');
        const params = new URLSearchParams(url?.toString().split('?')[1]);
        if (params.get('isOverLap') === 'no') {
          tab.isNotAllowedToView = true;
        }
      }
      return tab;
    });
    const getTabs = async () => {
      try {
        const tabs = await Promise.all(tabsCollection);
        let tabsList = [homeTab, ...tabsDashboard, ...tabs];
        if (isTargetWorkspace) {
          tabsList = [...tabsDashboard, ...tabs, homeTab];
          if (homeRecruiterAnalyticsStatus) {
            tabsList = [recruiterAnalyticsTab, ...tabsList];
          }
        }
        setEntitiesTabList(tabsList);
        pinned.current = tabsList;
        handleSelectedTab(selectedTab);
      } catch (e) {
        console.log(e);
      }
    };
    getTabs();
  }, [collections, dashboards, homeRecruiterAnalyticsStatus]);

  const handleUnpin = () => {
    let setNextInList = false;
    let tabToSet = null as IPinnedEntityTab | null;
    let entityId = null as string | null;
    if (isTargetWorkspace && homeRecruiterAnalyticsStatus && selectedPinnedId === '') {
      pinRecruiterAnalytics(
        setPinRecruiterAnalyticsData,
        setHomeRecruiterAnalyticsStatus,
        setNotification,
        pinRecruiterAnalyticsData,
        homeRecruiterAnalyticsStatus
      );
      tabToSet = entitesTabList[1];
      entityId = entitesTabList[1].id ?? null;
      entityAfterUnpin.current = { tab: tabToSet, entityId };
      setPinnedEntities({ collections: collections, dashboards: dashboards });
      return;
    }
    const filterCondn = (pd: IPinnedDashboardCollection | IPinnedDashboard) => {
      if (pd.id === selectedPinnedId) {
        setNextInList = true;
        return false;
      }
      if (setNextInList) {
        tabToSet = entitesTabList.find((tb) => tb.id === pd.id) ?? null;
        if (!isEmpty((pd as IPinnedDashboardCollection).dashboardCollection)) {
          entityId = (pd as IPinnedDashboardCollection).dashboardCollection.id ?? null;
        } else if (!isEmpty((pd as IPinnedDashboard).dashboard)) {
          entityId = (pd as IPinnedDashboard).dashboard.id ?? null;
        }
        setNextInList = false;
      }
      return true;
    };
    //here order is important for running filter 1.dashboard 2.masterDb same as order(by type) of tabs
    const pinnedDashboards = dashboards.filter(filterCondn);
    const pinnedCollections = collections.filter(filterCondn);
    if ((pinnedDashboards.length > 0 || pinnedCollections.length > 0) && tabToSet === null) {
      if (pinnedCollections.length > 0) {
        const lastCollection = pinnedCollections[pinnedCollections.length - 1];
        tabToSet = entitesTabList.find((tb) => tb.id === lastCollection.id) ?? null;
        entityId =
          tabToSet?.tabType === IPinnedEntityEnum.DASHBOARD_COLLECTION
            ? lastCollection.dashboardCollection.id ?? null
            : null;
      } else if (pinnedDashboards.length > 0) {
        const lastDashboard = pinnedDashboards[pinnedDashboards.length - 1];
        tabToSet = entitesTabList.find((tb) => tb.id === lastDashboard.id) ?? null;
        entityId =
          tabToSet?.tabType === IPinnedEntityEnum.DASHBOARD
            ? lastDashboard.dashboard.id ?? null
            : null;
      }
    }
    if (tabToSet === null) entityAfterUnpin.current = { tab: homeTab, entityId };
    else entityAfterUnpin.current = { tab: tabToSet, entityId };
    setPinnedEntities({ collections: pinnedCollections, dashboards: pinnedDashboards });
  };

  const renderSelectedEntity = {
    [IPinnedEntityEnum.DASHBOARD_COLLECTION]: !entitesTabList?.[selectedTab]?.isNotAllowedToView ? (
      <RenderDashboardsTabs key={selectedTab} showFilterTags={false} />
    ) : (
      <Grid>
        <NoDataFoundCard text="You cannot access analytics data as the account manager is not part of any mutual teams" />
      </Grid>
    ),
    [IPinnedEntityEnum.DASHBOARD]: (
      <AnalyticsDashboardViewer key={selectedTab} requireTitleBanner={false} />
    ),
  };

  const renderPinComponent = () => {
    if (entitesTabList?.[selectedTab]?.tabType === IPinnedEntityEnum.DASHBOARD) {
      return (
        <UnpinDashboard selectId={selectedDashboard?.id ?? ''} handleUnpinDashboard={handleUnpin} />
      );
    } else if (entitesTabList?.[selectedTab]?.tabType === IPinnedEntityEnum.DASHBOARD_COLLECTION) {
      return (
        <PinDashboardCollectionButton
          filters={null}
          entityId={null}
          entityName={entitesTabList?.[selectedTab]?.tabName}
          tabChange={handleUnpin}
          homeUnpin={true}
          pinDashboardId={entitesTabList?.[selectedTab]?.id}
        />
      );
    }
    return null;
  };
  useEffect(() => {
    (async () => {
      if (isTargetWorkspace) {
        try {
          const res = await getRecruiterAnalyticsStatus();
          setPinRecruiterAnalyticsData(res.data);
          setHomeRecruiterAnalyticsStatus(res.data?.pinned);
        } catch (e) {
          console.log(e);
        }
      }
    })();
  }, [isTargetWorkspace]);

  return (
    <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
      <Box bgcolor="white" boxShadow={1} borderRadius={1} display="flex" alignItems="center">
        <ScrollableTabs
          scrollList={entitesTabList}
          getTabIndex={handleSelectedTab}
          defaultValue={selectedTab}
          scrollButtons={false}
        />
        {entitesTabList.length < 9 && (
          <WsElementWithPopOver
            element={
              <Box ml={'-35px'} pr={2}>
                <WsButton size="small" startIcon={<AddIcon fontSize="medium" />}>
                  Add
                </WsButton>
              </Box>
            }
          >
            <Stack p={1}>
              <ListItemButton
                onClick={() => {
                  navigate('/workspace-users-and-teams/your-dashboards');
                }}
              >
                <WsIconText
                  text={'Pin Dashboard'}
                  icon={
                    <div style={{ transform: 'rotate(45deg)' }}>
                      <PushPinOutlinedIcon />
                    </div>
                  }
                  textSize={WsTypoVariants.BODY1}
                  textCursor="pointer"
                />
              </ListItemButton>
              <ListItemButton
                onClick={() => {
                  navigate('/workspace-users-and-teams/your-dashboards/view');
                }}
              >
                <WsIconText
                  text={'Create new Dashboard'}
                  icon={<DashboardCustomizeOutlinedIcon />}
                  textSize={WsTypoVariants.BODY1}
                  textCursor="pointer"
                />
              </ListItemButton>
            </Stack>
          </WsElementWithPopOver>
        )}
      </Box>
      {isSelectedTabHome && isTargetWorkspace && homeRecruiterAnalyticsStatus && (
        <ComposableDashboardFiltersContextProvider>
          <>
            <UnpinDashboard selectId={''} handleUnpinDashboard={handleUnpin} fromHome={true} />
            <UserProfileOverviewTemp recruiterId={user.userDTO?.email} />
          </>
        </ComposableDashboardFiltersContextProvider>
      )}

      {(isSelectedTabEntitiesLast && isTargetWorkspace) ||
      (isSelectedTabHome && !isTargetWorkspace) ? (
        <>
          {!isTargetWorkspace && <UserGuide />}
          <ReminderBoard />
          {/* <JobBoard />
        <ComAndAIBoard />
        <RecruiterActivity />
        <CandidatePipeline /> */}
        </>
      ) : (
        <ComposableDashboardFiltersContextProvider>
          <StageStatusContextProvider>
            {isDashboard(true)}
            {entitesTabList?.[selectedTab] && renderPinComponent()}
            {entitesTabList?.[selectedTab] &&
              renderSelectedEntity[entitesTabList?.[selectedTab]?.tabType]}
          </StageStatusContextProvider>
        </ComposableDashboardFiltersContextProvider>
      )}
    </ErrorBoundary>
  );
};

export default HomePageDashBoard;
